// The helm keys of the season map (R2707, ui-map-interface.md §9 "The
// helm keys"). Pure, so a test drives it with a clock of its own.
//
// The helm view of the sea view steers one hull with the STEER order
// (numbers.md §4.2, R2638): a wanted heading and a step of the engine
// telegraph, under a lease that each send renews. The map takes the same
// order with the arrow keys of `[keys.card]`, while one own fleet at sea
// is selected (no flag, R2830):
//
//   * left and right turn the ordered heading `MAP_HELM.turnStepDeg` a
//     press, port and starboard. A held key repeats at the rate of the
//     keyboard. The heading is the sim's atan2 sense, +x east and y to
//     the south, so a larger angle turns the bow to starboard.
//   * up and down step the telegraph from 0 (STOP) to `topStep` (FULL).
//   * the first key starts from the bow and from the step of the speed,
//     so a turn does not stop a hull that runs (`stepOf`, the rule of the
//     helm view).
//
// A change goes at most once each `sendMs`, and the order goes again each
// `refreshSec`, inside the lease. The helm keeps one record a fleet, so a
// hull keeps its run when the commander selects another. A record lets go
// when the wire shows its fleet on another order after it showed STEER (a
// new order of the commander), when the wire never shows STEER within
// `confirmSec` (a refusal), or when the fleet is gone. Then nothing more
// goes, and the lease of the server ends the last order: the hull holds
// where it brakes to rest, as a closed tab leaves it.

import { Mission } from '../gen/proto/aquaphobia';
import { MAP_HELM } from '../gen/presentation';
import { wrapAngle } from '../render/bow';

/** The helm keys, the ids of `[keys.card]` group `helm`. */
export type HelmKey = 'steer_port' | 'steer_starboard' | 'telegraph_up' | 'telegraph_down';

/** What the helm reads of one own fleet at sea. */
export interface HelmFleet {
  id: number;
  /** The bow, radians in the atan2 sense of the sim. */
  headingRad: number;
  /** The speed now and the top speed of the class, px a wall second. */
  speedPxS: number;
  topPxS: number;
  /** The standing order on the wire (`Mission`). */
  mission: number;
}

/** The figures of the helm, `MAP_HELM` of the pack. */
export type HelmCfg = { readonly [K in keyof typeof MAP_HELM]: number };

/** One fleet under the helm keys. */
interface HelmRecord {
  heading: number;
  step: number;
  /** A change waits to go. */
  dirty: boolean;
  /** When the order went last, ms; -Infinity before the first send. */
  sentAt: number;
  /** When the wire last showed the fleet on STEER, or the first press. */
  seenAt: number;
  /** The wire showed the fleet on STEER at least once. */
  confirmed: boolean;
}

/** The telegraph step of a hull at `speedPxS` of a top speed of `topPxS`. */
export function stepOf(speedPxS: number, topPxS: number, cfg: HelmCfg = MAP_HELM): number {
  if (!(topPxS > 0) || !(speedPxS > 0)) return 0;
  const share = speedPxS / topPxS;
  if (share < cfg.stopShare) return 0;
  return Math.min(cfg.topStep, Math.max(1, Math.round(share * cfg.topStep)));
}

/** The heading the card states: whole degrees of the compass, 0 north,
 *  90 east, from the sim's atan2 angle with y to the south. */
export function compassDeg(rad: number): number {
  const deg = Math.round((rad * 180) / Math.PI + 90);
  return ((deg % 360) + 360) % 360;
}

/** Sends one STEER: the fleet, the heading in milliradians, the step. */
export type SteerSend = (fleetId: number, headingMrad: number, step: number) => void;

export class MapHelm {
  private readonly recs = new Map<number, HelmRecord>();

  constructor(private readonly send: SteerSend, private readonly cfg: HelmCfg = MAP_HELM) {}

  /** One press of a helm key on a fleet. It always takes the press. */
  press(f: HelmFleet, key: HelmKey, nowMs: number): void {
    let r = this.recs.get(f.id);
    if (!r) {
      r = {
        heading: wrapAngle(f.headingRad), step: stepOf(f.speedPxS, f.topPxS, this.cfg),
        dirty: true, sentAt: -Infinity, seenAt: nowMs, confirmed: false,
      };
      this.recs.set(f.id, r);
    }
    const turn = (this.cfg.turnStepDeg * Math.PI) / 180;
    switch (key) {
      case 'steer_port': r.heading = wrapAngle(r.heading - turn); break;
      case 'steer_starboard': r.heading = wrapAngle(r.heading + turn); break;
      case 'telegraph_up': r.step = Math.min(this.cfg.topStep, r.step + 1); break;
      case 'telegraph_down': r.step = Math.max(0, r.step - 1); break;
    }
    r.dirty = true;
    this.flush(f.id, r, nowMs);
  }

  /**
   * One frame: lets go of a record whose fleet left STEER or is gone, and
   * sends a waiting change and the refresh. `look` reads a fleet, or null.
   */
  tick(nowMs: number, look: (id: number) => HelmFleet | null): void {
    for (const [id, r] of this.recs) {
      const f = look(id);
      if (!f) { this.recs.delete(id); continue; }
      if (f.mission === Mission.MISSION_STEER) {
        r.confirmed = true;
        r.seenAt = nowMs;
      } else if (r.confirmed || nowMs - r.seenAt > this.cfg.confirmSec * 1000) {
        this.recs.delete(id);
        continue;
      }
      this.flush(id, r, nowMs);
    }
  }

  /** The ordered heading in compass degrees and the step of a fleet under
   *  the helm keys, for the card; null for a fleet the helm does not hold. */
  read(fleetId: number): { headingDeg: number; step: number; top: number } | null {
    const r = this.recs.get(fleetId);
    return r ? { headingDeg: compassDeg(r.heading), step: r.step, top: this.cfg.topStep } : null;
  }

  /** The helm holds this fleet. */
  holds(fleetId: number): boolean {
    return this.recs.has(fleetId);
  }

  private flush(id: number, r: HelmRecord, nowMs: number): void {
    const since = nowMs - r.sentAt;
    if (r.dirty ? since < this.cfg.sendMs : since < this.cfg.refreshSec * 1000) return;
    r.dirty = false;
    r.sentAt = nowMs;
    this.send(id, Math.round(wrapAngle(r.heading) * 1000), r.step);
  }
}
